/**
 * Data persistence and synchronization types
 */

import type { Character } from './character';

export interface PersistenceOptions {
  location: StorageLocation;
  autoSave: boolean;
  syncInterval?: number;
}

export enum StorageLocation {
  Local = 'local',
  GoogleDrive = 'googleDrive'
}

export interface CharacterSummary {
  id: string;
  name: string;
  career: string;
  level: number;
  lastModified: Date;
  location: StorageLocation;
  syncStatus: {
    synced: boolean;
    pending: boolean;
    conflict: boolean;
    error: boolean;
    offline: boolean;
  };
}

export enum SyncStatus {
  Synced = 'synced',
  Pending = 'pending',
  Conflict = 'conflict',
  Error = 'error',
  Offline = 'offline'
}

export interface SaveResult {
  success: boolean;
  location: StorageLocation;
  timestamp: Date;
  error?: string;
  locations?: StorageLocation[];
  warnings?: string[];
  queued?: boolean;
}

export interface LoadResult {
  success: boolean;
  character?: Character;
  location: StorageLocation;
  timestamp: Date;
  error?: string;
  schemaVersion?: number;
  skills?: unknown[];
  talents?: unknown[];
  migrated?: boolean;
  recoveredFrom?: StorageLocation;
  repaired?: boolean;
}

export interface SyncOperation {
  id: string;
  type: SyncOperationType;
  characterId: string;
  timestamp: Date;
  retryCount: number;
  maxRetries: number;
  data?: unknown;
}

export enum SyncOperationType {
  Save = 'save',
  Load = 'load',
  Delete = 'delete'
}

export interface SyncQueue {
  operations: SyncOperation[];
  isProcessing: boolean;
  lastSync: Date;
}

export interface ConflictResolution {
  characterId: string;
  localVersion: Character;
  remoteVersion: Character;
  resolution: ConflictResolutionType;
  mergedVersion?: Character;
  timestamp: Date;
}

export enum ConflictResolutionType {
  UseLocal = 'useLocal',
  UseRemote = 'useRemote',
  CreateCopy = 'createCopy',
  Merge = 'merge'
}

export interface GoogleDriveConfig {
  clientId: string;
  apiKey: string;
  scope: string;
  discoveryDoc: string;
}

export interface GoogleDriveAuth {
  isSignedIn: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: Date;
}

export interface BackupMetadata {
  id: string;
  characterId: string;
  timestamp: Date;
  version: number;
  size: number;
  location: StorageLocation;
}

// Utility types for persistence
export type CharacterId = string;
export type BackupId = string;
export type SyncTimestamp = Date;

// Summary type alias for compatibility
export type Summary = CharacterSummary;
